import type { ReactNode } from "react";
import { cn } from "./utils";

export type BadgeVariant = "unread" | "mention" | "muted" | "missed";

const variants: Record<BadgeVariant, string> = {
  unread: "bg-gold text-ink",
  mention: "bg-gold text-ink",
  muted: "bg-ink-muted text-white",
  missed: "bg-scarlet text-white",
};

const spoken: Record<BadgeVariant, (count: number) => string> = {
  unread: (n) => `${n} unread`,
  mention: () => "You were mentioned",
  muted: (n) => `${n} unread, muted`,
  missed: (n) => `${n} missed`,
};

export type BadgeProps = {
  variant?: BadgeVariant;
  /** The number shown. Mention badges show @ instead. */
  count?: number;
  size?: "sm" | "md";
  /** A ring in the colour of the surface behind, for badges that overlap an icon. */
  ringClassName?: string;
  label?: string;
  className?: string;
};

export function Badge({
  variant = "unread",
  count = 0,
  size = "md",
  ringClassName,
  label,
  className,
}: BadgeProps) {
  const text = variant === "mention" ? "@" : count > 99 ? "99+" : String(count);
  return (
    <span
      role="img"
      aria-label={label ?? spoken[variant](count)}
      className={cn(
        "inline-flex flex-none items-center justify-center rounded-full font-bold leading-none box-border tabular-nums",
        size === "md" ? "h-[22px] min-w-[22px] px-[7px] text-xs" : "h-5 min-w-5 px-[5px] text-[11px]",
        ringClassName && cn("border-2", ringClassName),
        variants[variant],
        className,
      )}
    >
      {text}
    </span>
  );
}

// Inline @name inside a message. Mentions glow gold.
export function MentionChip({ children }: { children: ReactNode }) {
  return <span className="rounded-md bg-gold-tint px-[5px] font-semibold text-gold-ink">{children}</span>;
}

// Small label next to a name, such as "Assistant".
export function Tag({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex flex-none items-center whitespace-nowrap rounded-md bg-blue-tint px-[7px] py-px text-[11px] font-bold text-blue",
        className,
      )}
    >
      {children}
    </span>
  );
}
